import { Button, Checkbox, Drawer, Text } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { sdk } from "@/lib/sdk"
import { SearchInput, Thumb } from "./ui"

/** Shopify's "Browse products / collections" picker: search, tick, Add. */
export function ResourcePicker({
  kind,
  selected,
  onDone,
  onClose,
  title,
}: {
  kind: "products" | "collections"
  selected: string[]
  onDone: (ids: string[]) => void
  onClose: () => void
  title?: string
}) {
  const [q, setQ] = useState("")
  const [picked, setPicked] = useState<Set<string>>(new Set(selected))
  const { data, isLoading } = useQuery({
    queryKey: ["picker", kind, q],
    queryFn: async () =>
      kind === "products"
        ? (await sdk.admin.product.list({ q: q || undefined, limit: 50, fields: "id,title,thumbnail,status" } as any)).products.map((p: any) => ({ id: p.id, title: p.title, image: p.thumbnail, sub: p.status === "published" ? "" : p.status }))
        : (await sdk.admin.productCollection.list({ q: q || undefined, limit: 50, fields: "id,title,+metadata" } as any)).collections.map((c: any) => ({ id: c.id, title: c.title, image: c.metadata?.image, sub: "" })),
  })
  const toggle = (id: string, on: boolean) =>
    setPicked((s) => {
      const n = new Set(s)
      on ? n.add(id) : n.delete(id)
      return n
    })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{title ?? (kind === "products" ? "Add products" : "Add collections")}</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-3 overflow-y-auto">
          <SearchInput value={q} onChange={setQ} placeholder={`Search ${kind}`} />
          {isLoading && <Text size="small" className="text-ui-fg-muted">Loading…</Text>}
          <ul className="divide-y rounded-lg border">
            {data?.map((r: any) => (
              <li key={r.id}>
                <label className="flex cursor-pointer items-center gap-x-3 px-3 py-2 hover:bg-ui-bg-base-hover">
                  <Checkbox checked={picked.has(r.id)} onCheckedChange={(c) => toggle(r.id, c === true)} />
                  <Thumb src={r.image} size={32} />
                  <Text size="small" className="flex-1 truncate">{r.title}</Text>
                  {r.sub && <Text size="xsmall" className="text-ui-fg-muted">{r.sub}</Text>}
                </label>
              </li>
            ))}
            {data && !data.length && <li className="px-3 py-6 text-center text-[13px] text-ui-fg-muted">Nothing found</li>}
          </ul>
        </Drawer.Body>
        <Drawer.Footer>
          <Text size="small" className="mr-auto text-ui-fg-subtle">{picked.size} selected</Text>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" onClick={() => (onDone([...picked]), onClose())}>Done</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

/** Selected items as removable rows, with names looked up. */
export function useResourceNames(kind: "products" | "collections", ids: string[]) {
  return useQuery({
    queryKey: ["names", kind, ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const rows =
        kind === "products"
          ? (await sdk.admin.product.list({ id: ids, limit: ids.length, fields: "id,title,thumbnail" } as any)).products.map((p: any) => ({ id: p.id, title: p.title, image: p.thumbnail }))
          : (await sdk.admin.productCollection.list({ id: ids, limit: ids.length, fields: "id,title,+metadata" } as any)).collections.map((c: any) => ({ id: c.id, title: c.title, image: c.metadata?.image }))
      return Object.fromEntries(rows.map((r: any) => [r.id, r])) as Record<string, { id: string; title: string; image?: string | null }>
    },
  })
}
